<template>
  <div
    class="fixed bottom-4 z-120 origin-bottom-left transition-[left] duration-500 ease-out max-[1040px]:hidden"
    :class="sidebarHovered ? 'left-83.5' : 'left-20.75'"
    :style="animationVars"
  >
    <section
      :class="cardClasses"
      :aria-label="badgeText"
      role="button"
      tabindex="0"
      :aria-expanded="isExpanded"
      @click="expandCard"
      @keydown.enter.prevent="expandCard"
      @keydown.space.prevent="expandCard"
    >
      <header :class="headerClasses">
        <span
          class="inline-flex min-h-7 items-center justify-center rounded-[0.625rem] border border-success-300 bg-success-600/10 px-2.5 py-0.5 text-xs leading-none font-medium whitespace-nowrap text-success-600"
          >{{ badgeText }}</span
        >
        <button
          type="button"
          :class="collapseButtonClasses"
          :aria-label="$t('user_panel.subscription_card_collapse_aria')"
          :aria-hidden="!isExpanded"
          :tabindex="isExpanded ? 0 : -1"
          :disabled="!isExpanded"
          @click.stop="collapseCard"
        >
          <span class="h-0.75 w-6 rounded-full bg-primary-600" />
        </button>
      </header>

      <div :class="bodyClasses">
        <p :class="statusClasses">{{ statusText }}</p>

        <div
          :class="detailsClasses"
          :style="detailsTransition"
          aria-live="polite"
        >
          <div
            v-if="isTrialStatus"
            class="mt-1 mb-5 h-1.5 w-full overflow-hidden rounded-full bg-neutral-300"
            role="progressbar"
            :aria-label="$t('user_panel.subscription_card_progress_aria')"
            :aria-valuenow="trialProgressPercent"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div
              class="h-full rounded-full bg-success-500 transition-[width] duration-300 ease-out"
              :style="{ width: `${trialProgressPercent}%` }"
            />
          </div>

          <p
            class="mt-3! mb-1.5! text-sm leading-[1.3] font-medium text-neutral-700"
          >
            {{ headlineText }}
          </p>
          <p :class="descriptionClasses">
            {{ descriptionText }}
          </p>

          <button
            v-if="showUpgradeButton"
            type="button"
            :disabled="checkoutLoading"
            :class="[
              'mx-auto mt-4! block min-h-10 w-5/6 rounded-sm border-none bg-primary-600 p-2 text-base leading-[1.2] font-normal text-white',
              checkoutLoading
                ? 'cursor-not-allowed opacity-70'
                : 'cursor-pointer opacity-100',
            ]"
            @click.stop="$emit('upgradeClick')"
          >
            {{
              checkoutLoading
                ? $t('user_panel.subscription_card_redirecting')
                : upgradeButtonText
            }}
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { isTrialingStatus } from '~~/shared/billing/subscription-status'

export default {
  props: {
    sidebarHovered: {
      type: Boolean,
      default: false,
    },
    subscriptionStatus: {
      type: String,
      default: 'none',
    },
    trialDaysRemaining: {
      type: Number,
      default: 0,
    },
    trialTotalDays: {
      type: Number,
      default: 7,
    },
    checkoutLoading: {
      type: Boolean,
      default: false,
    },
    instanceVendor: {
      type: String,
      default: 'laniqo',
    },
  },
  emits: ['upgradeClick'],
  data() {
    return {
      isExpanded: false,
      animationDurationMs: 1000,
    }
  },
  computed: {
    isTrialStatus() {
      return isTrialingStatus(this.subscriptionStatus)
    },
    isActiveStatus() {
      return (this.subscriptionStatus || '').toLowerCase() === 'active'
    },
    animationVars() {
      return {
        '--trial-animation-duration': `${this.animationDurationMs}ms`,
        '--trial-animation-delay': `${Math.round(this.animationDurationMs * 0.23)}ms`,
      }
    },
    statusText() {
      if (this.isTrialStatus) {
        const days = Number.isFinite(this.trialDaysRemaining)
          ? Math.max(0, Math.floor(this.trialDaysRemaining))
          : 0

        if (days <= 0) {
          return this.$t('user_panel.subscription_card_trial_ended')
        }

        return this.$t(
          days === 1
            ? 'user_panel.subscription_card_day_left'
            : 'user_panel.subscription_card_days_left',
          { days }
        )
      }
      if (this.isActiveStatus) {
        return this.$t('user_panel.subscription_card_active')
      }
      return this.$t('user_panel.subscription_card_unsubscribed')
    },
    badgeText() {
      if (this.isTrialStatus) {
        return this.$t('user_panel.subscription_card_free_trial')
      }
      if (this.isActiveStatus) {
        return this.$t('user_panel.subscription_card_active_plan')
      }
      return this.$t('user_panel.subscription_card_subscription')
    },
    headlineText() {
      if (this.isTrialStatus) {
        return this.$t('user_panel.subscription_card_trial_ends_soon')
      }
      if (this.isActiveStatus) {
        return this.$t('user_panel.subscription_card_subscription_active')
      }
      return this.$t('user_panel.subscription_card_no_active_subscription')
    },
    descriptionText() {
      if (this.isTrialStatus) {
        return this.$t('user_panel.subscription_card_upgrade_description')
      }
      if (this.isActiveStatus) {
        return this.$t('user_panel.subscription_card_full_access_description')
      }
      return this.$t('user_panel.subscription_card_choose_plan_description')
    },
    trialProgressPercent() {
      const total = Number.isFinite(this.trialTotalDays)
        ? Math.max(1, Math.floor(this.trialTotalDays))
        : 7
      const remaining = Number.isFinite(this.trialDaysRemaining)
        ? Math.max(0, Math.floor(this.trialDaysRemaining))
        : 0
      const percent = (remaining / total) * 100

      return Math.max(0, Math.min(100, Math.round(percent)))
    },
    showUpgradeButton() {
      if (this.isTrialStatus) {
        return true
      }
      return !this.isActiveStatus
    },
    upgradeButtonText() {
      return this.instanceVendor === 'laniqo'
        ? this.$t('user_panel.subscription_card_manage_subscription')
        : this.$t('user_panel.subscription_card_upgrade_options')
    },
    cardClasses() {
      const base =
        'w-fit overflow-hidden rounded-xl border border-primary-300 bg-neutral-50 text-left transition-[padding,box-shadow] duration-[var(--trial-animation-duration)] ease-[cubic-bezier(0.2,0,0,1)]'

      if (this.isExpanded) {
        return `${base} cursor-default p-0 shadow-[0_0.625rem_1.5rem_color-mix(in_srgb,var(--color-primary-900)_26%,transparent)]`
      }

      return `${base} cursor-pointer px-3 pt-3 pb-3 shadow-[0_0.375rem_0.875rem_color-mix(in_srgb,var(--color-primary-900)_14%,transparent)]`
    },
    headerClasses() {
      const base =
        'relative flex items-center transition-[padding,border-color] duration-[var(--trial-animation-duration)] ease-[cubic-bezier(0.2,0,0,1)]'

      if (this.isExpanded) {
        return `${base} justify-between border-b border-neutral-300 px-4 pt-4 pb-[0.9rem]`
      }

      return `${base} justify-start border-b border-transparent px-0 pt-0 pb-0`
    },
    collapseButtonClasses() {
      const base =
        'absolute right-4 top-4 inline-flex h-6 w-6 items-center justify-center rounded-full bg-transparent cursor-pointer transition-[opacity,transform] duration-250 ease-out'

      if (this.isExpanded) {
        return `${base} pointer-events-auto translate-y-0 opacity-100 delay-[var(--trial-animation-duration)]`
      }

      return `${base} pointer-events-none translate-y-0 opacity-0 delay-0`
    },
    bodyClasses() {
      const base =
        'contain-inline-size overflow-hidden text-neutral-700 transition-[padding,min-width,max-height] duration-[var(--trial-animation-duration)] ease-[cubic-bezier(0.2,0,0,1)]'

      if (this.isExpanded) {
        return `${base} min-w-[16rem] max-h-[20rem] p-5`
      }

      return `${base} min-w-0 max-h-[2.5rem] p-0`
    },
    statusClasses() {
      const base =
        'block w-fit relative font-medium text-primary-900 transition-[margin,font-size,transform,translate,left] duration-[var(--trial-animation-duration)] ease-[cubic-bezier(0.2,0,0,1)] will-change-[transform,left] text-sm'

      if (this.isExpanded) {
        return `${base} mt-0! mb-2! leading-[1.12] left-full -translate-x-full`
      }

      return `${base} mt-2.5! mb-2! leading-[1.2] left-0 translate-x-0`
    },
    detailsClasses() {
      const base = 'min-w-[13.5rem] will-change-[opacity,transform]'

      if (this.isExpanded) {
        return `${base} translate-y-0 opacity-100`
      }

      return `${base} translate-y-[0.35rem] opacity-0`
    },
    detailsTransition() {
      const dur = 'var(--trial-animation-duration)'
      const ease = 'cubic-bezier(0.2,0,0,1)'
      const fadeDelay = this.isExpanded ? 'var(--trial-animation-delay)' : '0ms'

      return {
        transition: `opacity ${dur} ${ease} ${fadeDelay}, transform ${dur} ${ease} ${fadeDelay}`,
      }
    },
    descriptionClasses() {
      if (this.isTrialStatus) {
        return 'mb-4 text-xs leading-[1.35] text-neutral-600'
      }

      return this.showUpgradeButton
        ? 'mb-4 text-xs leading-[1.35] text-neutral-600'
        : 'mb-0 text-xs leading-[1.35] text-neutral-600'
    },
  },
  methods: {
    expandCard() {
      this.isExpanded = true
    },
    collapseCard() {
      this.isExpanded = false
    },
  },
}
</script>
